import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { CheckCircle, HourglassMedium, Receipt, WarningCircle } from "@phosphor-icons/react";
import { useNoIndex } from "../../hooks/usePageSeo";
import { functions } from "../../firebase";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import "./GrowPublic.css";

const POLL_MS = 4000;
const MAX_POLLS = 15; // ~1 minute — Grow's callback normally lands within seconds

/**
 * /pay/thanks?c=<clinicId>&l=<linkId> — Grow redirects here after payment
 * (successUrl). Shows clinic branding + "התקבל" once our callback flipped the
 * link to paid; polls briefly while "בעיבוד". No amounts, no patient data.
 * Error code: CK-6301
 */
export default function GrowThankYouPage() {
  useNoIndex();
  const [params] = useSearchParams();
  const clinicId = params.get("c");
  const linkId = params.get("l");
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const polls = useRef(0);

  useEffect(() => {
    if (!clinicId || !linkId) {
      setState({ loading: false, error: "bad_params", data: null });
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const res = await httpsCallable(functions, "getGrowThankYouPublic")({ clinicId, linkId });
        if (cancelled) return;
        const data = res.data || {};
        setState({ loading: false, error: null, data });
        if (!data.paid && polls.current < MAX_POLLS) {
          polls.current += 1;
          timer = window.setTimeout(load, POLL_MS);
        }
      } catch (error) {
        console.error("[CK-6301] Grow thank-you load failed", {
          errorCode: "CK-6301",
          errorMessage: error.message,
          code: error.code,
          timestamp: new Date().toISOString(),
        });
        if (!cancelled) setState({ loading: false, error: error.code === "functions/not-found" ? "not_found" : "error", data: null });
      }
    };
    load();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [clinicId, linkId]);

  const d = state.data;
  return (
    <main className="gpub" dir="rtl">
      <section className="gpub-card" aria-live="polite">
        {d?.logoUrl ? <img className="gpub-logo" src={d.logoUrl} alt="" /> : null}
        <span className="gpub-eyebrow">{d?.clinicName || "CliniKit"}</span>

        {state.loading && <LoaderLoop size="md" />}

        {!state.loading && d?.paid && (
          <>
            <span className="gpub-icon ok"><CheckCircle size={44} weight="fill" /></span>
            <h1>התשלום התקבל, תודה!</h1>
            <p>{d.receiptToPatient ? "הקבלה תישלח אליך בוואטסאפ או ב-SMS." : "הקליניקה קיבלה את התשלום. לקבלה ניתן לפנות לקליניקה."}</p>
            {d.hasReceipt && d.receiptToPatient && (
              <span className="gpub-secure"><Receipt size={13} weight="fill" /> הקבלה הופקה ונשלחת אליך</span>
            )}
          </>
        )}

        {!state.loading && d && !d.paid && (
          <>
            <span className="gpub-icon pending"><HourglassMedium size={44} weight="fill" /></span>
            <h1>התשלום בעיבוד</h1>
            <p>האישור יגיע בדקות הקרובות. אפשר לסגור את העמוד — הקליניקה תקבל את העדכון אוטומטית.</p>
            {polls.current < MAX_POLLS && <LoaderLoop size="sm" />}
          </>
        )}

        {!state.loading && state.error && (
          <>
            <span className="gpub-icon warn"><WarningCircle size={40} weight="fill" /></span>
            <h1>{state.error === "bad_params" || state.error === "not_found" ? "הקישור אינו תקין" : "משהו השתבש"}</h1>
            <p>אם שילמת, אל דאגה — התשלום נרשם אצל הקליניקה. לשאלות פנו לקליניקה.</p>
          </>
        )}

        {!state.loading && (
          <a className="gpub-footlink" href="/pay/terms" target="_blank" rel="noreferrer">תנאי תשלום</a>
        )}
      </section>
    </main>
  );
}
